import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, PackageX, ShoppingCart } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtPrice, roundFinalPriceUp } from "@/lib/totals";

/**
 * Iter 129 — Venta cruzada. Modal BLOQUEANTE mostrado tras crear una
 * reparación cuando existe una regla activa que coincide con Equipo+Modelo.
 *
 * Reglas críticas:
 *  - No cierra con X / ESC / clic fuera. El empleado DEBE tomar una decisión.
 *  - Anti-duplicado: el modal se abre SOLO tras obtener el `repair_id`
 *    definitivo. Este componente NUNCA crea la reparación — solo añade
 *    productos vía `POST /repairs/{id}/parts`.
 *  - Servicios NO controlan stock → siempre seleccionables.
 *  - Productos SIN stock aparecen deshabilitados con badge "SIN STOCK".
 *
 * Props:
 *   repairId       string  — repair_id ya creado (obligatorio).
 *   rule           object  — regla que coincidió (id, name, ...).
 *   products       array   — hidratados desde `GET /cross-sell/match`.
 *   modelLabel     string  — mostrado en el header ("iPhone 15 Pro").
 *   onFinished     fn      — llamado tras registrar el evento y cerrar.
 */
const REASONS = [
  { code: "in_hurry", label: "Cliente con prisa" },
  { code: "no_stock", label: "Producto sin stock" },
  { code: "already_has", label: "Ya tiene funda/protector" },
  { code: "not_applicable", label: "No aplica" },
  { code: "other", label: "Otro" },
];

export default function CrossSellModal({ repairId, rule, products, modelLabel, onFinished }) {
  const [selected, setSelected] = useState({});
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [showReasonPicker, setShowReasonPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const sendingRef = useRef(false);

  const toggle = (p) => {
    if (!p.in_stock) return;
    setSelected((s) => ({ ...s, [p.id]: !s[p.id] }));
  };

  const selectedList = useMemo(
    () => products.filter((p) => selected[p.id] && p.in_stock),
    [products, selected],
  );
  const totalRevenue = selectedList.reduce((sum, p) => sum + roundFinalPriceUp(p.sale_price), 0);

  // Iter 129 — Bloquear ESC.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const guarded = (fn) => async (...args) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setBusy(true);
    try { await fn(...args); }
    finally { sendingRef.current = false; setBusy(false); }
  };

  const submitEvent = async (payload) => {
    const r = await api.post("/cross-sell/events", payload);
    return r.data;
  };

  const addSelectedProducts = async () => {
    if (selectedList.length === 0) {
      toast.error("Selecciona al menos un producto o pulsa 'Cliente no interesado'.");
      return;
    }
    // Iter 130 — Guardar líneas PERSISTIDAS en la reparación existente
    // usando el MISMO endpoint que la edición manual (`PUT /repairs/{id}`
    // con `items: [...]`). Los campos siguen el schema `RepairItem` del
    // backend (product_id, sku, name, kind, quantity, unit_price,
    // discount_pct, vat_rate, warranty_months). El backend calcula
    // `line_total` y normaliza descuento (`_normalize_repair_items`).
    // Fusionamos con las líneas ya existentes de la reparación para no
    // pisar nada si la creación hubiera dejado alguna línea previa (en
    // una repair recién creada normalmente será `[]`).
    let existing = [];
    try {
      const cur = await api.get(`/repairs/${repairId}`);
      existing = Array.isArray(cur.data?.items) ? cur.data.items : [];
    } catch (err) {
      toast.error(`No se pudo cargar la reparación: ${formatApiError(err)}`);
      return;
    }
    const newItems = selectedList.map((p) => ({
      product_id: p.id,
      sku: p.sku || "",
      name: p.name || "",
      description: "",
      kind: p.kind || "product",
      quantity: 1,
      unit_price: Number(p.sale_price) || 0,
      discount_pct: 0,
      vat_rate: Number(p.vat_rate) || 21,
      warranty_months: Number(p.warranty_months) || 0,
    }));
    const merged = [...existing, ...newItems];
    if (merged.length > 10) {
      toast.error(
        "Máximo 10 líneas por reparación. Reduce la selección o gestiona las líneas desde la ficha.",
      );
      return;
    }
    try {
      await api.put(`/repairs/${repairId}`, { items: merged });
    } catch (err) {
      // Iter 130 — Falló el PUT. NO submitimos el evento y NO navegamos:
      // el modal permanece abierto para permitir reintentar. Nunca creamos
      // otra reparación (ya existe con repair_id fijo).
      toast.error(`No se pudieron guardar las líneas: ${formatApiError(err)}`);
      return;
    }
    // Registrar el evento SOLO tras confirmar el guardado.
    try {
      await submitEvent({
        repair_id: repairId,
        rule_id: rule.id,
        offered_product_ids: products.map((p) => p.id),
        accepted_product_ids: selectedList.map((p) => p.id),
        revenue_generated: totalRevenue,
        result: "sale_completed",
      });
    } catch (err) {
      // El guardado de líneas ya se hizo con éxito; sólo notificamos el
      // fallo del log de auditoría sin bloquear el flujo.
      console.warn("[CrossSell] Evento no registrado:", err);
    }
    toast.success(`${selectedList.length} producto(s) añadido(s) — Venta cruzada registrada.`);
    onFinished && onFinished({ result: "sale_completed", added: selectedList.length });
  };

  const declineCustomer = async () => {
    await submitEvent({
      repair_id: repairId,
      rule_id: rule.id,
      offered_product_ids: products.map((p) => p.id),
      accepted_product_ids: [],
      result: "customer_declined",
    });
    toast.info("Registrado: cliente no interesado.");
    onFinished && onFinished({ result: "customer_declined", added: 0 });
  };

  const submitNotOffered = async () => {
    if (!reasonCode) {
      toast.error("Selecciona un motivo.");
      return;
    }
    if (reasonCode === "other" && !reasonText.trim()) {
      toast.error("Escribe el motivo cuando seleccionas 'Otro'.");
      return;
    }
    await submitEvent({
      repair_id: repairId,
      rule_id: rule.id,
      offered_product_ids: products.map((p) => p.id),
      accepted_product_ids: [],
      result: "not_offered",
      reason_code: reasonCode,
      reason_text: reasonText.trim(),
    });
    toast.info("Registrado: oferta no realizada.");
    onFinished && onFinished({ result: "not_offered", added: 0 });
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4"
      data-testid="cross-sell-modal"
      // Iter 129 — bloquear clic fuera.
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white border-2 border-[#111110] shadow-hard w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 bg-[#6E8B3D] text-white">
          <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">
            Oportunidad de venta cruzada
          </div>
          <h2 className="font-display text-xl font-black flex items-center gap-2 mt-0.5">
            <ShoppingCart size={18} /> VENTA CRUZADA — {modelLabel}
          </h2>
        </div>

        {!showReasonPicker ? (
          <>
            <div className="px-5 py-3 flex-1 overflow-y-auto">
              <p className="text-sm text-[#525252] mb-3">
                Productos recomendados para este modelo. Marca los que el cliente
                quiera añadir a la reparación.
              </p>
              <ul className="space-y-2">
                {products.map((p) => (
                  <li
                    key={p.id}
                    data-testid={`cross-sell-item-${p.id}`}
                    className={`flex items-center gap-3 border px-3 py-2 ${
                      !p.in_stock ? "border-[#E5E5E0] bg-[#F7F7F4] opacity-60" : "border-[#111110]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selected[p.id]}
                      disabled={!p.in_stock}
                      onChange={() => toggle(p)}
                      data-testid={`cross-sell-check-${p.id}`}
                      className="w-4 h-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">
                        {p.name}
                        {!p.in_stock && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-700 text-white font-mono text-[9px] uppercase tracking-widest"
                            data-testid={`cross-sell-out-of-stock-${p.id}`}
                          >
                            <PackageX size={9} /> Sin stock
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-[#525252]">
                        {p.sku} · {p.kind === "service" ? "Servicio" : `Stock: ${p.stock}`}
                      </div>
                    </div>
                    <div className="font-semibold text-sm">
                      {fmtPrice(roundFinalPriceUp(p.sale_price))}
                    </div>
                  </li>
                ))}
              </ul>
              {selectedList.length > 0 && (
                <div className="mt-3 pt-2 border-t border-[#E5E5E0] text-sm text-right">
                  Total: <b>{fmtPrice(totalRevenue)}</b>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E0] bg-[#F7F7F4] flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowReasonPicker(true)}
                disabled={busy}
                data-testid="cross-sell-btn-not-offered"
                className="px-3 py-1.5 text-sm border border-[#525252] text-[#525252] hover:bg-white disabled:opacity-40"
              >
                No se ha ofrecido
              </button>
              <button
                type="button"
                onClick={guarded(declineCustomer)}
                disabled={busy}
                data-testid="cross-sell-btn-declined"
                className="px-3 py-1.5 text-sm border border-[#111110] hover:bg-[#111110] hover:text-white disabled:opacity-40"
              >
                Cliente no interesado
              </button>
              <button
                type="button"
                onClick={guarded(addSelectedProducts)}
                disabled={busy || selectedList.length === 0}
                data-testid="cross-sell-btn-add"
                className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
              >
                <Check size={14} /> Añadir seleccionados ({selectedList.length})
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="px-5 py-3 flex-1 overflow-y-auto">
              <p className="text-sm mb-2 inline-flex items-center gap-1">
                <AlertTriangle size={13} className="text-yellow-600" />
                Selecciona el motivo por el que no se ofreció venta cruzada:
              </p>
              <div className="space-y-1">
                {REASONS.map((r) => (
                  <label
                    key={r.code}
                    className="flex items-center gap-2 border border-[#E5E5E0] px-3 py-2 text-sm cursor-pointer hover:bg-[#F7F7F4]"
                  >
                    <input
                      type="radio"
                      name="cs-reason"
                      value={r.code}
                      checked={reasonCode === r.code}
                      onChange={() => setReasonCode(r.code)}
                      data-testid={`cross-sell-reason-${r.code}`}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              {reasonCode === "other" && (
                <textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  rows={2}
                  placeholder="Describe el motivo..."
                  data-testid="cross-sell-reason-text"
                  className="mt-2 w-full border border-[#E5E5E0] focus:border-[#6E8B3D] px-2 py-1 text-sm outline-none"
                />
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E0] bg-[#F7F7F4] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowReasonPicker(false); setReasonCode(""); setReasonText(""); }}
                disabled={busy}
                className="px-3 py-1.5 text-sm border border-[#525252] hover:bg-white"
              >
                Atrás
              </button>
              <button
                type="button"
                onClick={guarded(submitNotOffered)}
                disabled={busy || !reasonCode}
                data-testid="cross-sell-not-offered-save"
                className="bg-[#111110] hover:bg-[#000] text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
              >
                Guardar motivo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
